import { useT } from "@transifex/react";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import IconButton from "../../../basic/iconButton/IconButton";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import OnlineTestStudentName from "./OnlineTestStudentName";
import OnlineTestTimer from "./OnlineTestTimer";

interface OnlineTestHeaderProps {
    timeRemaining?: string;
    isTimeRunningOut?: boolean;
    shouldBounceTimer?: boolean;
    onBounceEnd?: () => void;
    /** Kept visible for the whole test. Absent for public classes and teacher previews. */
    studentName?: string;
    onOverviewClick?: () => void;
    onClose?: () => void;
}

const OnlineTestHeader = (props: OnlineTestHeaderProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint, isAboveMD } = useBreakpoints();

    const t_overview = t("Overview", { _tags: "OnlineTest,Header" });
    const t_sr_close = t("Close overview", { _tags: "OnlineTest, ScreenReaderOnly" });

    // useCountdown leaves the time empty whenever only a distant deadline is set.
    const hasTimer = !!props.timeRemaining;
    // A close handler means the overview, which stacks the name under the timer rather than beside it.
    const isOverview = !!props.onClose;
    const isNameCentered = !isOverview && isAboveMD && hasTimer && !!props.studentName;

    // Without a timer the name takes its slot, so the row never starts with a gap.
    const renderTimerSlot = () => {
        if (!hasTimer) return <OnlineTestStudentName studentName={props.studentName} />;

        return (
            <OnlineTestTimer
                timeRemaining={props.timeRemaining}
                isTimeRunningOut={props.isTimeRunningOut}
                shouldBounceTimer={props.shouldBounceTimer}
                onBounceEnd={props.onBounceEnd}
            />
        );
    };

    const renderOverviewLink = () => (
        <button
            className="flex flex-row items-center gap-xs bg-transparent border-none cursor-pointer"
            onClick={props.onOverviewClick}
            aria-label={t_overview}
            data-testid="online-test-overview-button"
        >
            <span className="text-l text-p6-grey-dark whitespace-nowrap">{t_overview}</span>
            <PhaseSixIcon
                name="chevron-right"
                className="text-[30px] text-p6-grey-dark"
            />
        </button>
    );

    const headerBaseClass = `max-w-[1000px] mx-auto gap-s w-full ${getStyleBasedOnBreakpoint(
        Breakpoint.MD,
        "px-m",
        "px-s"
    )}`;

    // Timer and name share the sticky header so neither scrolls under it with the exercise list.
    if (isOverview) {
        // Only a real two-row stack aligns to the top; a lone timer or name centres on the close button.
        const isStacked = hasTimer && !!props.studentName;

        return (
            <header
                className={`${headerBaseClass} flex flex-row justify-between ${
                    isStacked ? "items-start" : "items-center"
                }`}
            >
                <div className="flex flex-col gap-xxs min-w-no">
                    {renderTimerSlot()}
                    {isStacked && (
                        <OnlineTestStudentName
                            studentName={props.studentName}
                            size="text-m"
                        />
                    )}
                </div>
                <IconButton
                    iconName="close"
                    ariaLabel={t_sr_close}
                    onClick={props.onClose}
                    size="text-xxl"
                />
            </header>
        );
    }

    // Only the name's track shrinks, so a long name ellipsises while the sides keep it centred.
    if (isNameCentered) {
        return (
            <header className={`${headerBaseClass} grid grid-cols-[1fr_minmax(0,auto)_1fr] items-center h-l`}>
                {renderTimerSlot()}
                <div className="flex justify-center min-w-no">
                    <OnlineTestStudentName studentName={props.studentName} />
                </div>
                <div className="flex justify-end">{renderOverviewLink()}</div>
            </header>
        );
    }

    // With a timer on mobile the name lives in the layout's sub header row, so this row is timer and
    // link only. The slot shrinks first, so a long name in it truncates before the link is squeezed.
    return (
        <header className={`${headerBaseClass} flex flex-row items-center justify-between h-l`}>
            <div className="min-w-no">{renderTimerSlot()}</div>
            {renderOverviewLink()}
        </header>
    );
};

export default OnlineTestHeader;
